import { CalloutConfig } from '../pages/main-page';
import { LitElement } from 'lit';
import { getMainPage } from './page-name';
import { isDefined } from './utils';

/**
 * Simple helper to register a callout directly
 * Use this when you already have an element reference
 *
 * @param config - Callout configuration with targetElement
 *
 * @example
 * // Query element and register callout
 * const button = this.shadowRoot?.querySelector('#my-button') as Element;
 * registerCallout({
 *   id: CalloutId.MyTooltip,
 *   targetElement: button,
 *   content: 'Click here!',
 *   priority: 80
 * });
 */
export function registerCallout(config: CalloutConfig): void {
    if (!config.targetElement && config.position !== 'center') {
        console.warn(`[registerCallout] No target element provided for callout: ${config.id}`);
        return;
    }

    getMainPage()?.showCallout(config);
}

/**
 * Register a callout after a component finishes updating
 * Perfect for use in firstUpdated() or connectedCallback()
 *
 * @param config - Callout configuration
 * @param context - LitElement context
 *
 * @example
 * async firstUpdated() {
 *   await this.updateComplete;
 *   const button = this.shadowRoot?.querySelector('#my-button') as Element;
 *   registerCalloutAfterUpdate({
 *     id: CalloutId.MyTooltip,
 *     targetElement: button,
 *     content: 'Click here!',
 *   }, this);
 * }
 */
export async function registerCalloutAfterUpdate(
    config: CalloutConfig,
    context?: LitElement
): Promise<void> {
    if (context) {
        await context.updateComplete;
    }
    registerCallout(config);
}

/**
 * Register multiple callouts at once
 *
 * @param configs - Array of callout configurations
 *
 * @example
 * const btn1 = document.querySelector('#btn1') as Element;
 * const btn2 = document.querySelector('#btn2') as Element;
 * registerMultipleCallouts([
 *   { id: CalloutId.Tooltip1, targetElement: btn1, content: 'First', priority: 90 },
 *   { id: CalloutId.Tooltip2, targetElement: btn2, content: 'Second', priority: 80 },
 * ]);
 */
export function registerMultipleCallouts(
    configs: CalloutConfig[]
): void {
    configs.forEach(config => registerCallout(config));
}

/**
 * Public interface for components using CalloutMixin
 */
export interface ICalloutMixin {
    registerCallout: (config: CalloutConfig) => void;
    registerCalloutAfterUpdate: (config: CalloutConfig) => Promise<void>;
    registerMultipleCallouts: (configs: CalloutConfig[]) => void;
}

/**
 * Mixin to add callout registration capability to any LitElement
 *
 * @example
 * class MyComponent extends CalloutMixin(LitElement) {
 *   firstUpdated() {
 *     const button = this.shadowRoot?.querySelector('#my-button') as Element;
 *     this.registerCallout({
 *       id: CalloutId.MyTooltip,
 *       targetElement: button,
 *       content: 'Click here!'
 *     });
 *   }
 * }
 */
export function CalloutMixin<T extends new (...args: any[]) => LitElement>(
    Base: T
): T & (new (...args: any[]) => ICalloutMixin) {
    return class extends Base {
        /**
         * Register a callout in this component's shadow DOM
         */
        registerCallout(config: CalloutConfig): void {
            registerCallout(config);
        }

        /**
         * Register a callout after the component updates
         */
        async registerCalloutAfterUpdate(config: CalloutConfig): Promise<void> {
            await registerCalloutAfterUpdate(config, this);
        }

        /**
         * Register multiple callouts in this component
         */
        registerMultipleCallouts(configs: CalloutConfig[]): void {
            registerMultipleCallouts(configs);
        }
    } as T & (new (...args: any[]) => ICalloutMixin);
}

/**
 * Decorator to automatically register a callout when a component renders
 *
 * @example
 * @customElement('my-component')
 * class MyComponent extends LitElement {
 *   @callout({
 *     id: CalloutId.MyTooltip,
 *     targetElement: this.shadowRoot?.querySelector('#my-button') as Element,
 *     content: 'Click here!',
 *     priority: 80
 *   })
 *   firstUpdated() {
 *     // Callout automatically registered after this runs
 *   }
 * }
 */
export function callout(config: CalloutConfig) {
    return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
        const originalMethod = descriptor.value;

        descriptor.value = async function (this: LitElement, ...args: any[]) {
            const result = await originalMethod.apply(this, args);
            await registerCalloutAfterUpdate(config, this);
            return result;
        };

        return descriptor;
    };
}

/**
 * Walkthrough state manager - keeps track of multi-step walkthroughs
 */
class WalkthroughManager {
    private currentStep = 0;
    private steps: CalloutConfig[] = [];
    private context?: LitElement;
    private walkthroughId: string = '';

    startWalkthrough(
        id: string,
        steps: CalloutConfig[],
        context?: LitElement
    ) {
        // Check if walkthrough was already completed
        const mainPage = getMainPage();
        if (mainPage?.isCalloutDismissed(id)) {
            return;
        }

        this.walkthroughId = id;
        this.steps = steps;
        this.context = context;
        this.currentStep = 0;
        this.showCurrentStep();
    }

    private showCurrentStep() {
        if (this.currentStep >= this.steps.length) {
            // Walkthrough complete
            return;
        }

        const step = this.steps[this.currentStep];
        const isLastStep = this.currentStep === this.steps.length - 1;
        const isFirstStep = this.currentStep === 0;

        // Build navigation actions array with Next/Back buttons
        const navigationActions = [];

        if (!isFirstStep) {
            navigationActions.push({
                text: '← Back',
                handler: () => this.previousStep(),
                primary: false
            });
        }

        if (!isLastStep) {
            navigationActions.push({
                text: 'Next →',
                handler: () => this.nextStep(),
                primary: true
            });
        } else {
            navigationActions.push({
                text: '✓ Done',
                handler: () => this.completeWalkthrough(),
                primary: true
            });
        }

        // Merge custom actions from step config with navigation actions
        const allActions = [...(step.actions ?? []), ...navigationActions];

        // Add step counter to title
        const stepTitle = step.title
            ? step.title
            : `Step ${this.currentStep + 1} of ${this.steps.length}`;

        // Preserve the step's onDismiss callback if provided
        const stepOnDismiss = step.onDismiss;

        registerCallout({
            ...step,
            id: `${this.walkthroughId}-step-${this.currentStep}`,
            type: 'walkthrough',
            title: stepTitle,
            actions: allActions,
            dismissible: true,
            priority: 100, // Always show walkthrough steps at highest priority
            currentStep: this.currentStep,
            totalSteps: this.steps.length,
            onDismiss: () => {
                // Call the step's custom onDismiss if provided
                if (stepOnDismiss) {
                    stepOnDismiss();
                }
                this.completeWalkthrough();
            }
        });
    }

    private nextStep() {
        // Clear onDismiss callback to prevent recursion
        const mainPage = getMainPage();
        if (isDefined(mainPage)) {
            const callout = mainPage.activeCallouts.find(c => c.id === `${this.walkthroughId}-step-${this.currentStep}`);
            if (callout) callout.onDismiss = undefined;
            // Don't persist dismissal - allow going back
            mainPage.dismissCallout(`${this.walkthroughId}-step-${this.currentStep}`, false);
        }
        this.currentStep++;
        this.showCurrentStep();
    }

    private previousStep() {
        // Clear onDismiss callback to prevent recursion
        const mainPage = getMainPage();
        if (isDefined(mainPage)) {
            const callout = mainPage.activeCallouts.find(c => c.id === `${this.walkthroughId}-step-${this.currentStep}`);
            if (callout) callout.onDismiss = undefined;
            // Don't persist dismissal - allow revisiting steps
            mainPage.dismissCallout(`${this.walkthroughId}-step-${this.currentStep}`, false);
        }
        this.currentStep--;
        this.showCurrentStep();
    }

    private completeWalkthrough() {
        // Call the current step's onDismiss callback before completing
        const currentStep = this.steps[this.currentStep];
        if (currentStep?.onDismiss) {
            currentStep.onDismiss();
        }

        // Find and clear the onDismiss callback to prevent infinite recursion
        const mainPage = getMainPage();
        if (isDefined(mainPage)) {
            const calloutId = `${this.walkthroughId}-step-${this.currentStep}`;
            const callout = mainPage.activeCallouts.find(c => c.id === calloutId);
            if (callout) {
                callout.onDismiss = undefined; // Clear callback before dismissing
            }
            // Dismiss current step without persisting
            mainPage.dismissCallout(calloutId, false);

            // Mark the entire walkthrough as dismissed so it doesn't show again
            mainPage.markCalloutDismissed(this.walkthroughId);
        }
        // Reset state
        this.currentStep = 0;
        this.steps = [];
        this.walkthroughId = '';
    }
}

// Singleton instance
const walkthroughManager = new WalkthroughManager();

/**
 * Register a multi-step walkthrough that guides users through features
 * Shows one step at a time with Next/Back navigation
 *
 * @param id - Unique ID for this walkthrough
 * @param steps - Array of callout configurations for each step
 * @param context - Optional LitElement context for shadow DOM support
 *
 * @example
 * registerWalkthroughAfterUpdate('onboarding', [
 *   {
 *     targetSelector: '#nav',
 *     title: 'Navigation',
 *     content: 'Use this to navigate between sections'
 *   },
 *   {
 *     targetSelector: '#add-button',
 *     title: 'Add Points',
 *     content: 'Click here to add points to your map'
 *   },
 *   {
 *     targetElement: publishButton,
 *     title: 'Share',
 *     content: 'Publish your map when ready!'
 *   }
 * ], this);
 */
export async function registerWalkthroughAfterUpdate(
    id: string,
    steps: CalloutConfig[],
    context?: LitElement
): Promise<void> {
    if (context) {
        await context.updateComplete;
    }

    // Start the walkthrough
    walkthroughManager.startWalkthrough(id, steps, context);
}
